import React, {
    useCallback, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ICON_TYPE_CLOSE_SLIM } from 'ui-library/components/Icon';
import { FEE_TYPES } from 'datasource/CreatePortfolio/constants';
import { Modal, Table, Icon } from '../../../../../../../../../../ui-library';
import {
    CHANGE_HISTORY_TYPE, getCommissionChangeHistoryColumns, getGeneralAgencyChangeHistoryColumns,
    getPbNumberChangeHistoryColumns,
} from '../../constants';
import { useGetCustomService } from '../../../../../../../../../../hooks/rest/useGetCustomService';
import Preloader from '../../../../../../../../../../components/Preloader';
import useFormatting from '../../../../../../../../../../locale/useFormatting';

const DEFAULT_PAGE_SIZE = 5;

const ChangeHistoryModal = ({
    type, onClose, portfolioId, selectedFeeType, investmentApplicationId,
}: any) => {
    const [currentPage, setCurrentPage] = useState(1);
    const { t } = useTranslation();
    const { getFormattedDate } = useFormatting();

    const { data: pbHistoryData, isLoading: pbHistoryLoading } = useGetCustomService({
        service: 'planService',
        api: 'getPbNumberChangeHistory',
        payload: { portfolioId, pageNumber: currentPage, pageSize: DEFAULT_PAGE_SIZE },
        enabled: type === CHANGE_HISTORY_TYPE.pbNumber,
    });

    const { data: agencyHistoryData, isLoading: agencyHistoryLoading } = useGetCustomService({
        service: 'planService',
        api: 'getGeneralAgencyChangeHistory',
        payload: { portfolioId, pageNumber: currentPage, pageSize: DEFAULT_PAGE_SIZE },
        enabled: type === CHANGE_HISTORY_TYPE.generalAgency,
    });

    const { data: feesHistoryData, isLoading: feesHistoryLoading } = useGetCustomService({
        service: 'planService',
        api: 'getFeesChangeHistory',
        payload: {
            investmentApplicationId, pageNumber: currentPage, pageSize: DEFAULT_PAGE_SIZE, feeType: selectedFeeType,
        },
        enabled: type === CHANGE_HISTORY_TYPE.commission,
    });

    const tableData = useMemo(() => {
        switch (type) {
            case CHANGE_HISTORY_TYPE.pbNumber: return pbHistoryData?.data?.pbNumberHistory;
            case CHANGE_HISTORY_TYPE.generalAgency: return agencyHistoryData?.data?.generalAgencyHistory;
            case CHANGE_HISTORY_TYPE.commission: return feesHistoryData?.data?.items;
            default: return undefined;
        }
    }, [type, pbHistoryData?.data?.pbNumberHistory, agencyHistoryData?.data?.generalAgencyHistory, feesHistoryData?.data?.items]);

    const totalRows = useMemo(() => {
        switch (type) {
            case CHANGE_HISTORY_TYPE.pbNumber: return pbHistoryData?.data?.total;
            case CHANGE_HISTORY_TYPE.generalAgency: return agencyHistoryData?.data?.totalNumber;
            case CHANGE_HISTORY_TYPE.commission: return feesHistoryData?.data?.total;
            default: return undefined;
        }
    }, [type, pbHistoryData?.data?.total, agencyHistoryData?.data?.totalNumber, feesHistoryData?.data?.total]);

    // eslint-disable-next-line no-nested-ternary
    const inProgress = type === CHANGE_HISTORY_TYPE.pbNumber ? pbHistoryLoading
        : type === CHANGE_HISTORY_TYPE.generalAgency ? agencyHistoryLoading
            : feesHistoryLoading;

    const tableColumns = useMemo(() => {
        switch (type) {
            case CHANGE_HISTORY_TYPE.pbNumber:
                return getPbNumberChangeHistoryColumns(t, getFormattedDate);
            case CHANGE_HISTORY_TYPE.generalAgency:
                return getGeneralAgencyChangeHistoryColumns(t, getFormattedDate);
            case CHANGE_HISTORY_TYPE.commission:
                return getCommissionChangeHistoryColumns(t, getFormattedDate, selectedFeeType !== FEE_TYPES.issueCommission);
            default:
                return [];
        }
    }, [getFormattedDate, t, type, selectedFeeType]);

    const onChangePage = useCallback((paging: any) => {
        setCurrentPage(paging?.currentPage);
    }, []);

    const closeIcon = useMemo(() => (<Icon type={ICON_TYPE_CLOSE_SLIM} size={20} />), []);

    return (
        <Modal
            visible={!!type}
            title={t('planDetails.changeHistory.modalTitle')}
            onOk={onClose}
            onCancel={onClose}
            cancelText={null}
            width={700}
            okButtonProps={{ style: { minWidth: '100px', flex: 0 } }}
            className="change-history-modal"
            keyboard={false}
            maskClosable={false}
            closable
            borderRadius
            closeIcon={closeIcon}
        >
            <Preloader isLoading={inProgress}>
                <Table
                    columns={tableColumns}
                    data={tableData}
                    onChange={onChangePage}
                    paging={{
                        total: totalRows,
                        pageSize: DEFAULT_PAGE_SIZE,
                        currentPage,
                    }}
                />
            </Preloader>
        </Modal>
    );
};

export default ChangeHistoryModal;
